import { useContext, useEffect, useState } from 'react';
import { FormControl, FormControlLabel, Radio, RadioGroup, Stack, TextField, Typography } from '@mui/material';
import { DialogWrapper } from '../../presentation-components/display-elements-components/dialog-wrapper';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import DashboardService from '../../../shared/services/dashboard/dashboard.service';
import { IDashboard, IPostDashboard, WidgetVisibility } from '../../../shared/services/dashboard/dashboard.model';

interface DashboardDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (dashboard: IDashboard) => void;
  dashboard?: IDashboard; // present when renaming an existing dashboard (layout preserved)
}

const dashboardService = new DashboardService();

export const DashboardDialog: React.FC<DashboardDialogProps> = ({ open, onClose, onSuccess, dashboard }) => {
  const userNotificationsContext = useContext(UserNotificationsContext);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState<WidgetVisibility>('private');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(dashboard?.name || '');
    setDescription(dashboard?.description || '');
    setVisibility(dashboard?.visibility || 'private');
  }, [open, dashboard]);

  const handleSubmit = async () => {
    const payload: IPostDashboard = {
      name: name.trim(),
      description: description.trim() || null,
      visibility,
      // Preserve the existing layout when renaming; a new dashboard starts empty.
      layout: dashboard?.layout || [],
    };
    try {
      setIsSubmitting(true);
      const saved = dashboard
        ? await dashboardService.edit(dashboard.id, payload)
        : await dashboardService.create(payload);
      onSuccess(saved);
      onClose();
    } catch (e: any) {
      userNotificationsContext.openSnackbar({
        open: true,
        message: e?.response?.data?.message || 'Something went wrong',
        backgroundColor: '#70787A',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DialogWrapper
      open={open}
      onClose={onClose}
      title={dashboard ? 'Rename dashboard' : 'New dashboard'}
      cancelHandler={onClose}
      submitHandler={handleSubmit}
      isSubmitting={isSubmitting}
      disableSaveButton={!name.trim()}
      maxWidth="xs"
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} fullWidth size="small" />
        <TextField label="Description (optional)" value={description} onChange={(e) => setDescription(e.target.value)} fullWidth size="small" multiline minRows={2} />
        <FormControl>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>Visibility</Typography>
          <RadioGroup row value={visibility} onChange={(e) => setVisibility(e.target.value as WidgetVisibility)}>
            <FormControlLabel value="private" control={<Radio />} label="Only me" />
            <FormControlLabel value="organisation" control={<Radio />} label="Share with organisation" />
          </RadioGroup>
        </FormControl>
      </Stack>
    </DialogWrapper>
  );
};
